<template>
  <div class="container mt-4">
    <TopNav>
      <NavItem
        icon="far fa-save"
        :text="$t('GENERAL.BUTTON.SAVE')"
        :pulse="isDirty"
        :loading="inProgress"
        @click.native="updateCodeEmbedScripts"
      />
    </TopNav>
    <div class="row mt-4">
      <div class="col-12">
        <h1>{{ $t('SETTINGS.TITLE.CUSTOM_CODE_EMBED') }}</h1>
        <p class="mt-3">
          {{ $t('SETTINGS.TEXT.ADD_CUSTOM_CODE_SNIPPETS_TO_YOUR_STUDIO_PAGE') }}
        </p>
      </div>
      <div class="col-12">
        <hr />
        <div
          class="d-flex flex-nowrap flex-row justify-content-between align-items-center"
        >
          <div>
            <i class="far fa-info-circle c-gra mr-2"></i>
            <span class="p help-link c-charcoal">
              {{ $t('SETTINGS.TEXT.ENABLE_CUSTOM_CODE_EMBED') }}
            </span>
          </div>
          <HMSwitch
            v-model="codeEmbed.code_embed_enabled"
            @change="toggleCodeEmbed"
          ></HMSwitch>
        </div>
      </div>
      <div class="col-10">
        <div class="small my-2 c-medium-gray mr-5">
          {{ $t('SETTINGS.TEXT.CUSTOM_CODE_WARNING') }}
        </div>
      </div>
      <div class="col-12">
        <hr />
      </div>
      <div v-if="codeEmbed.code_embed_enabled" class="col-12 mt-3">
        <h3 class="mb-0">{{ $t('SETTINGS.LABEL.CUSTOM_CODE') }}</h3>
        <HMTextarea
          v-model="codeEmbed.global_scripts"
          :maxLength="65535"
          :rows="10"
          :useDynamicHeight="false"
        />
      </div>
    </div>
  </div>
</template>

<script>
import TopNav from '@/web/components/navigation/TopNav';
import NavItem from '@/web/components/navigation/NavItem';
import Vue from 'vue';
import { mapGetters } from 'vuex';
import DirtyMixin from '@/web/mixins/DirtyMixin';
import { errorToMessage } from '@/core/utils/errors';

export default Vue.extend({
  components: {
    TopNav,
    NavItem,
  },
  name: 'CodeEmbed',
  mixins: [DirtyMixin],
  computed: {
    ...mapGetters({
      teacherStudioUrl: 'user/teacherStudioUrl',
      codeEmbed: 'setting/codeEmbed',
    }),
  },
  data() {
    return {
      inProgress: true,
    };
  },
  methods: {
    async loadData() {
      try {
        await this.$store.dispatch('setting/getCodeEmbed');
        this.clearDirty();
      } finally {
        this.inProgress = false;
      }
    },
    async toggleCodeEmbed(value) {
      this.inProgress = true;
      try {
        await this.$store.dispatch('setting/toggleCodeEmbed', value);
        this.clearDirty();
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(
            error.response,
            `Failed to toggle code embed: ${error}`
          ),
          duration: -1, // stay ON
        });
      } finally {
        this.inProgress = false;
      }
    },
    async updateCodeEmbedScripts() {
      try {
        this.inProgress = true;
        await this.$store.dispatch(
          'setting/updateCodeEmbedScripts',
          this.codeEmbed.global_scripts
        );
        this.clearDirty();
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('SETTINGS.NOTIFICATIONS.CODE_EMBED_SUCCESS'),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(
            error.response,
            `Failed to save code embed: ${error}`
          ),
          duration: -1, // stay ON
        });
      } finally {
        this.inProgress = false;
      }
    },
  },
  async mounted() {
    await this.loadData();
  },
  watch: {
    codeEmbed: {
      handler: function (codeEmbed, oldCodeEmbed) {
        if (oldCodeEmbed) {
          this.setDirty();
        }
      },
      deep: true,
    },
  },
});
</script>

<style lang="scss">
@import '@/web/styles/components-variables.scss';
.help-banner {
  color: $dark-gray;
  background-color: $lightest-gray;
  padding: 0.5rem 1rem;
  border-radius: $border-radius;
}
</style>
<style scoped>
.help-link ::v-deep a {
  color: #7a8390;
  text-decoration: underline;
}
</style>
